import React from 'react'
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Checkbox,
  FormGroup,
  Typography,
} from '@mui/material'
import tw from 'twin.macro'
import { RiskMinor } from '@shopify/polaris-icons'

interface Props {
  name?: string
  label?: string
  value?: (string | number | boolean)[]
  error?: boolean
  helperText?: string | false
  options: Array<BasicRadioOption>
  onChange(e: React.ChangeEvent<any>): void
  dataTestId?: string
  disabled?: boolean
}

export interface BasicRadioOption {
  label: string
  value: string | number | boolean
}

export const BasicCheckboxSelect = ({
  name,
  label,
  value,
  error,
  helperText,
  options,
  onChange,
  dataTestId,
  disabled,
}: Props) => {
  // Same option row as `OAuthScopesField`: a quiet `shades-100` outline that turns purple with
  // a `primary-25` fill when ticked; the label stays black either way. Red outline on error.
  const getContainerStyle = (option: BasicRadioOption) => {
    if (error) {
      return tw`border-critical-500`
    }

    if (value?.includes(option.value)) {
      return tw`border-primary-700 bg-primary-25`
    }

    return tw`border-shades-100`
  }

  return (
    <FormControl css={tw`w-full`} error={error} disabled={disabled}>
      {label ? (
        // Same anatomy as every input label (`h6 text-shades-400 mb-1`).
        <FormLabel id={name} css={tw`mb-1`}>
          <Typography variant="h6" css={tw`text-shades-400`} component="span">
            {label}
          </Typography>
        </FormLabel>
      ) : null}
      <FormGroup
        aria-labelledby={name}
        onChange={onChange}
        css={tw`gap-2`}
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value + option.label}
            value={option.value}
            // MUI only forwards checked/name/onChange/value/inputRef to the wrapped control when
            // they are defined on the label. Without `checked` the Checkbox stays uncontrolled,
            // so a pre-filled `value` renders unticked and the first click toggles it away.
            checked={Boolean(value?.includes(option.value))}
            disabled={disabled}
            css={[
              tw`m-0 w-full rounded-lg border border-solid py-1 pl-1 pr-3`,
              getContainerStyle(option),
            ]}
            control={<Checkbox />}
            label={
              <Typography variant="body2" css={tw`w-full text-shades-1000`}>
                {option.label}
              </Typography>
            }
          />
        ))}
      </FormGroup>
      {!!error && !!helperText && (
        <FormHelperText css={tw`ml-0`} component="div">
          <div css={tw`flex items-center gap-1`}>
            <RiskMinor width={20} css={tw`fill-critical-500`} />
            <Typography variant="body2" css={tw`text-critical-500`} component="span">
              {helperText}
            </Typography>
          </div>
        </FormHelperText>
      )}
    </FormControl>
  )
}
